import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Phone } from 'lucide-react';
import { supabase } from '../../services/supabaseClient';
import { serviceRoleClient } from '../../services/serviceRoleClient';
import CallRecordingPlayer from '../CallRecordingPlayer';

const db = () => serviceRoleClient || supabase;

const last10 = (phone) => {
  const d = String(phone || '').replace(/\D/g, '');
  return d.length >= 10 ? d.slice(-10) : d;
};

const SARV_BASE = 'https://s-ct3.sarv.com/v2/recording/direct';
const SARV_IDS = ['11965974', '68798380'];

const parseJson = (value, fallback) => {
  try {
    if (value == null) return fallback;
    if (typeof value === 'string') {
      const s = value.trim();
      if (!s) return fallback;
      return JSON.parse(s);
    }
    return value;
  } catch {
    return fallback;
  }
};

const buildSarvUrl = (file, sarvUserId) => {
  if (!file || typeof file !== 'string') return null;
  const path = file.trim();
  if (!path) return null;
  if (/^https?:\/\//i.test(path)) return path;
  const userId = String(sarvUserId || SARV_IDS[0]).trim() || SARV_IDS[0];
  const sep = path.startsWith('/') ? '' : '/';
  return `${SARV_BASE}/${userId}${sep}${path}`;
};

const extractRecordingUrls = (call) => {
  const out = [];
  const seen = new Set();
  const push = (url, label) => {
    if (!url || typeof url !== 'string') return;
    const trimmed = url.trim();
    if (!trimmed || seen.has(trimmed)) return;
    if (!/^https?:\/\//i.test(trimmed)) return;
    seen.add(trimmed);
    out.push({ url: trimmed, label: label || `Recording ${out.length + 1}` });
  };

  const sarvUserId = call?.sarv_user_id;
  if (call?.sf_recording_url) push(call.sf_recording_url, call.sf_recording_name || 'Tata recording');

  const recs = Array.isArray(call?.recordings) ? call.recordings : parseJson(call?.recordings, []);
  if (Array.isArray(recs)) {
    recs.forEach((r, i) => {
      if (typeof r === 'string') {
        if (/^https?:\/\//i.test(r)) push(r, `Recording ${i + 1}`);
        else push(buildSarvUrl(r, sarvUserId), `Sarv recording ${i + 1}`);
      } else if (r && typeof r === 'object') {
        const directUrl = r.url || r.recording || r.recording_url || r.location;
        if (directUrl && /^https?:\/\//i.test(String(directUrl))) {
          push(directUrl, r.name || r.label);
        } else if (r.file || r.path) {
          push(buildSarvUrl(r.file || r.path, sarvUserId), r.name || r.label || `Sarv recording ${i + 1}`);
        }
      }
    });
  }

  if (!out.length && recs?.[0]?.file) {
    SARV_IDS.forEach((id) => push(buildSarvUrl(recs[0].file, id), 'Sarv recording'));
  }

  return out;
};

const statusLabel = (code) => {
  const map = {
    3: 'Both Answered',
    4: 'Customer Answered - Agent Unanswered',
    5: 'Customer Answered',
    6: 'Customer Unanswered - Agent Answered',
    8: 'Customer Unanswered',
    10: 'Agent Answered',
  };
  return map[code] || (code == null ? 'Unknown' : `Status ${code}`);
};

const typeLabel = (type) => {
  const map = { IBD: 'Inbound', OBD: 'Outbound', CTC: 'Click to Call', QC: 'Quick Call' };
  return map[type] || type || 'Call';
};

const fmtDate = (value) => {
  if (!value) return 'N/A';
  return new Date(value).toLocaleString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const fmtDuration = (seconds) => {
  const n = Number(seconds);
  if (!Number.isFinite(n) || n <= 0) return '0s';
  const m = Math.floor(n / 60);
  const s = Math.floor(n % 60);
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
};

const partyForCall = (call, customerPhones, mechanicPhones) => {
  const p = last10(call?.cnumber);
  if (p && mechanicPhones.has(p)) return 'Mechanic';
  if (p && customerPhones.has(p)) return 'Customer';
  return 'Unknown';
};

export default function ComplaintCallRecordings({
  requestId,
  customerPhone,
  alternatePhone,
  mechanicPhone,
  mechanicAltPhone,
  registeredAt,
  cancelledAt,
  completedAt,
  embedded = false,
}) {
  const customerPhones = useMemo(() => {
    const s = new Set();
    [customerPhone, alternatePhone].forEach((p) => {
      const n = last10(p);
      if (n) s.add(n);
    });
    return s;
  }, [customerPhone, alternatePhone]);

  const mechanicPhones = useMemo(() => {
    const s = new Set();
    [mechanicPhone, mechanicAltPhone].forEach((p) => {
      const n = last10(p);
      if (n) s.add(n);
    });
    return s;
  }, [mechanicPhone, mechanicAltPhone]);

  const { data: calls = [], isLoading } = useQuery({
    queryKey: [
      'complaintCallRecordings',
      requestId,
      [...customerPhones].join(','),
      [...mechanicPhones].join(','),
    ],
    queryFn: async () => {
      const client = db();
      const phones = [...customerPhones, ...mechanicPhones];
      const start = registeredAt
        ? new Date(new Date(registeredAt).getTime() - 2 * 60 * 60 * 1000).toISOString()
        : null;
      const endSource = cancelledAt || completedAt || new Date().toISOString();
      const end = new Date(new Date(endSource).getTime() + 24 * 60 * 60 * 1000).toISOString();

      const byId = new Map();
      const addRows = (rows) => {
        (rows || []).forEach((row) => {
          const id = row.callid || row.id;
          if (!id || byId.has(id)) return;
          byId.set(id, {
            ...row,
            recordings: Array.isArray(row.recordings) ? row.recordings : parseJson(row.recordings, []),
          });
        });
      };

      if (requestId) {
        const { data } = await client
          .from('call_reports')
          .select('*')
          .eq('service_request_id', requestId)
          .order('createdat', { ascending: false })
          .limit(80);
        addRows(data);
      }

      for (const p of phones) {
        let q = client
          .from('call_reports')
          .select('*')
          .like('cnumber', `*${p}`)
          .order('createdat', { ascending: false })
          .limit(40);
        if (start) q = q.gte('createdat', start);
        if (end) q = q.lte('createdat', end);
        const { data, error } = await q;
        if (error) {
          const retry = await client
            .from('call_reports')
            .select('*')
            .ilike('cnumber', `%${p}`)
            .order('createdat', { ascending: false })
            .limit(40);
          addRows(retry.data);
        } else {
          addRows(data);
        }
      }

      return Array.from(byId.values()).sort(
        (a, b) => new Date(b.createdat || 0).getTime() - new Date(a.createdat || 0).getTime(),
      );
    },
    enabled: !!requestId,
    staleTime: 30 * 1000,
  });

  return (
    <div className={embedded ? '' : 'bg-white rounded-xl shadow-sm border border-gray-200 p-6'}>
      {!embedded ? (
        <>
          <div className="flex items-center gap-2 mb-4">
            <Phone size={20} className="text-teal-600" />
            <h2 className="text-xl font-bold text-gray-900">Call recordings</h2>
            <span className="text-sm text-gray-500">({calls.length})</span>
          </div>
          <p className="text-sm text-gray-500 mb-4">
            Customer aur mechanic dono numbers par hue calls — inbound / outbound.
          </p>
        </>
      ) : (
        <p className="text-sm text-slate-500 mb-4">
          {calls.length} call{calls.length === 1 ? '' : 's'} · customer + mechanic numbers
        </p>
      )}
      {isLoading ? (
        <p className="text-sm text-gray-500">Loading calls…</p>
      ) : calls.length === 0 ? (
        <p className="text-sm text-gray-500">Is complaint se linked koi call recording nahi mili.</p>
      ) : (
        <div className="space-y-4">
          {calls.map((call) => {
            const recs = extractRecordingUrls(call);
            const party = partyForCall(call, customerPhones, mechanicPhones);
            return (
              <div key={call.callid || call.id} className="rounded-lg border border-gray-200 p-4">
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <span className="text-sm font-semibold text-gray-900">{fmtDate(call.createdat)}</span>
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700">
                    {typeLabel(call.ctype || call.cType)}
                  </span>
                  <span
                    className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                      party === 'Mechanic'
                        ? 'bg-violet-100 text-violet-800'
                        : party === 'Customer'
                          ? 'bg-sky-100 text-sky-800'
                          : 'bg-gray-100 text-gray-700'
                    }`}
                  >
                    {party}
                  </span>
                  <span className="text-xs text-gray-500">{statusLabel(call.callstatus)}</span>
                  <span className="text-xs text-gray-500">{fmtDuration(call.talkduration || call.ivrduration)}</span>
                </div>
                <p className="text-sm text-gray-700 mb-3">
                  Number: <span className="font-medium">{call.cnumber || 'N/A'}</span>
                </p>
                {recs.length ? (
                  recs.map((r) => (
                    <CallRecordingPlayer key={r.url} src={r.url} className="w-full mb-2" showDownload />
                  ))
                ) : (
                  <p className="text-xs text-gray-400">No recording file on this call.</p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
